import { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, PlusIcon, TrashIcon, PlayIcon } from '@/components/icons/NavIcons';
import { SimulationResults, SimulationResultData } from './SimulationResults';

// Types for simulation configuration
export interface AttributeLevel {
  level_code: string;
  level_name: string;
  level_description?: string;
  is_price_attribute?: boolean;
  value?: number | string;
}

export interface SimulationAttribute {
  attribute_code: string;
  attribute_name: string;
  levels: AttributeLevel[];
  isExpanded?: boolean;
  isIncluded?: boolean;
  selectedLevels?: string[];
}

export interface SimulationConfig {
  name: string;
  description?: string;
  simulation_type: 'market_entry' | 'portfolio_optimization' | 'sku_rationalization' | 'pricing' | 'launch';
  attributes: SimulationAttribute[];
  base_product?: string;
  competitor_products?: string[];
  segments?: string[];
}

interface SimulationConfiguratorProps {
  clientId: string;
  simulationType: string;
  onRunSimulation?: (config: SimulationConfig, result: SimulationResultData) => void;
  language?: string;
}

// Default attributes template - can be expanded to 25
const DEFAULT_ATTRIBUTES: SimulationAttribute[] = [
  {
    attribute_code: 'BRAND',
    attribute_name: 'Brand',
    levels: [
      { level_code: 'BR_CORONA', level_name: 'Corona', level_description: 'Premium Mexican import' },
      { level_code: 'BR_STELLA', level_name: 'Stella Artois', level_description: 'Belgian premium' },
      { level_code: 'BR_BUDWEISER', level_name: 'Budweiser', level_description: 'American premium' },
      { level_code: 'BR_BRAHMA', level_name: 'Brahma', level_description: 'Brazilian mainstream' },
      { level_code: 'BR_SKOL', level_name: 'Skol', level_description: 'Brazilian value leader' },
      { level_code: 'BR_ANTARCTICA', level_name: 'Antarctica', level_description: 'Brazilian traditional' },
      { level_code: 'BR_HEINEKEN', level_name: 'Heineken', level_description: 'Dutch premium competitor' },
      { level_code: 'BR_AMSTEL', level_name: 'Amstel', level_description: 'Dutch mainstream competitor' },
    ],
    isIncluded: true,
    selectedLevels: ['BR_CORONA', 'BR_STELLA', 'BR_BUDWEISER'],
  },
  {
    attribute_code: 'PRICE',
    attribute_name: 'Price Point (BRL)',
    levels: [
      { level_code: 'P_299', level_name: 'R$2.99', value: 2.99, is_price_attribute: true },
      { level_code: 'P_349', level_name: 'R$3.49', value: 3.49, is_price_attribute: true },
      { level_code: 'P_399', level_name: 'R$3.99', value: 3.99, is_price_attribute: true },
      { level_code: 'P_449', level_name: 'R$4.49', value: 4.49, is_price_attribute: true },
      { level_code: 'P_499', level_name: 'R$4.99', value: 4.99, is_price_attribute: true },
      { level_code: 'P_549', level_name: 'R$5.49', value: 5.49, is_price_attribute: true },
      { level_code: 'P_599', level_name: 'R$5.99', value: 5.99, is_price_attribute: true },
      { level_code: 'P_699', level_name: 'R$6.99', value: 6.99, is_price_attribute: true },
      { level_code: 'P_799', level_name: 'R$7.99', value: 7.99, is_price_attribute: true },
      { level_code: 'P_899', level_name: 'R$8.99', value: 8.99, is_price_attribute: true },
      { level_code: 'P_999', level_name: 'R$9.99', value: 9.99, is_price_attribute: true },
      { level_code: 'P_1199', level_name: 'R$11.99', value: 11.99, is_price_attribute: true },
    ],
    isIncluded: true,
    selectedLevels: ['P_399', 'P_499', 'P_599', 'P_699'],
  },
  {
    attribute_code: 'PACK_SIZE',
    attribute_name: 'Pack Size',
    levels: [
      { level_code: 'PS_269', level_name: '269ml Can', level_description: 'Single serve can' },
      { level_code: 'PS_350', level_name: '350ml Can', level_description: 'Standard can' },
      { level_code: 'PS_473', level_name: '473ml Can', level_description: 'Tall can' },
      { level_code: 'PS_355', level_name: '355ml Bottle', level_description: 'Standard longneck' },
      { level_code: 'PS_600', level_name: '600ml Bottle', level_description: 'Large bottle' },
      { level_code: 'PS_1L', level_name: '1L Bottle', level_description: 'Litrão' },
      { level_code: 'PS_6PK', level_name: '6-Pack', level_description: '6x355ml' },
      { level_code: 'PS_12PK', level_name: '12-Pack', level_description: '12x350ml' },
      { level_code: 'PS_18PK', level_name: '18-Pack', level_description: '18x350ml' },
      { level_code: 'PS_24PK', level_name: '24-Pack', level_description: '24x350ml' },
    ],
    isIncluded: true,
    selectedLevels: ['PS_350', 'PS_473', 'PS_600'],
  },
  {
    attribute_code: 'PACK_TYPE',
    attribute_name: 'Pack Type',
    levels: [
      { level_code: 'PT_CAN', level_name: 'Aluminum Can' },
      { level_code: 'PT_GLASS_CLR', level_name: 'Clear Glass Bottle' },
      { level_code: 'PT_GLASS_GRN', level_name: 'Green Glass Bottle' },
      { level_code: 'PT_GLASS_BRN', level_name: 'Brown Glass Bottle' },
      { level_code: 'PT_PET', level_name: 'PET Bottle' },
    ],
    isIncluded: true,
    selectedLevels: ['PT_CAN', 'PT_GLASS_CLR'],
  },
  {
    attribute_code: 'ALCOHOL',
    attribute_name: 'Alcohol Content',
    levels: [
      { level_code: 'ALC_0', level_name: '0.0% (Zero)', level_description: 'Non-alcoholic' },
      { level_code: 'ALC_LOW', level_name: '2.5%', level_description: 'Low alcohol' },
      { level_code: 'ALC_STD', level_name: '4.5%', level_description: 'Standard' },
      { level_code: 'ALC_MED', level_name: '5.0%', level_description: 'Medium' },
      { level_code: 'ALC_HIGH', level_name: '5.5%', level_description: 'Higher' },
      { level_code: 'ALC_STRONG', level_name: '6.0%+', level_description: 'Strong' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'FLAVOR',
    attribute_name: 'Flavor Profile',
    levels: [
      { level_code: 'FLV_LIGHT', level_name: 'Light & Crisp' },
      { level_code: 'FLV_BALANCED', level_name: 'Balanced' },
      { level_code: 'FLV_MALTY', level_name: 'Malty & Full' },
      { level_code: 'FLV_HOPPY', level_name: 'Hoppy & Bitter' },
      { level_code: 'FLV_WHEAT', level_name: 'Wheat & Fruity' },
      { level_code: 'FLV_DARK', level_name: 'Dark & Roasted' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'ORIGIN',
    attribute_name: 'Country of Origin',
    levels: [
      { level_code: 'OR_BRAZIL', level_name: 'Brazil' },
      { level_code: 'OR_MEXICO', level_name: 'Mexico' },
      { level_code: 'OR_BELGIUM', level_name: 'Belgium' },
      { level_code: 'OR_GERMANY', level_name: 'Germany' },
      { level_code: 'OR_NETHERLANDS', level_name: 'Netherlands' },
      { level_code: 'OR_USA', level_name: 'USA' },
      { level_code: 'OR_IRELAND', level_name: 'Ireland' },
      { level_code: 'OR_JAPAN', level_name: 'Japan' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'OCCASION',
    attribute_name: 'Occasion',
    levels: [
      { level_code: 'OCC_EVERYDAY', level_name: 'Everyday' },
      { level_code: 'OCC_SOCIAL', level_name: 'Social Gatherings' },
      { level_code: 'OCC_SPORTS', level_name: 'Sports Events' },
      { level_code: 'OCC_CELEBRATION', level_name: 'Celebrations' },
      { level_code: 'OCC_ROMANTIC', level_name: 'Romantic' },
      { level_code: 'OCC_RELAXATION', level_name: 'Relaxation' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'PROMO',
    attribute_name: 'Promotion Type',
    levels: [
      { level_code: 'PRM_NONE', level_name: 'No Promotion' },
      { level_code: 'PRM_10OFF', level_name: '10% Off' },
      { level_code: 'PRM_20OFF', level_name: '20% Off' },
      { level_code: 'PRM_BOGO', level_name: 'Buy 1 Get 1' },
      { level_code: 'PRM_BUNDLE', level_name: 'Bundle Deal' },
      { level_code: 'PRM_LOYALTY', level_name: 'Loyalty Points 2x' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'CHANNEL',
    attribute_name: 'Sales Channel',
    levels: [
      { level_code: 'CH_HYPER', level_name: 'Hypermarket' },
      { level_code: 'CH_SUPER', level_name: 'Supermarket' },
      { level_code: 'CH_CONV', level_name: 'Convenience Store' },
      { level_code: 'CH_BAR', level_name: 'Bar/Restaurant' },
      { level_code: 'CH_ONLINE', level_name: 'E-commerce' },
      { level_code: 'CH_CLUB', level_name: 'Club Store' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'TEMP',
    attribute_name: 'Serving Temperature',
    levels: [
      { level_code: 'TEMP_ICE', level_name: 'Ice Cold (0°C)' },
      { level_code: 'TEMP_COLD', level_name: 'Cold (3-5°C)' },
      { level_code: 'TEMP_COOL', level_name: 'Cool (8-10°C)' },
      { level_code: 'TEMP_CELLAR', level_name: 'Cellar (12-14°C)' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'MALT',
    attribute_name: 'Malt Type',
    levels: [
      { level_code: 'MLT_ADJUNCT', level_name: 'Adjunct (Corn/Rice)' },
      { level_code: 'MLT_PURE', level_name: 'Pure Malt' },
      { level_code: 'MLT_DUPLO', level_name: 'Duplo Malte' },
      { level_code: 'MLT_WHEAT', level_name: 'Wheat Malt' },
      { level_code: 'MLT_DARK', level_name: 'Dark Malt' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'CLAIM',
    attribute_name: 'Marketing Claim',
    levels: [
      { level_code: 'CL_REFRESH', level_name: 'Refreshing' },
      { level_code: 'CL_TRADITION', level_name: 'Traditional' },
      { level_code: 'CL_PREMIUM', level_name: 'Premium Quality' },
      { level_code: 'CL_NATURAL', level_name: 'Natural Ingredients' },
      { level_code: 'CL_LOWCAL', level_name: 'Low Calorie' },
      { level_code: 'CL_CRAFT', level_name: 'Craft Style' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'SEGMENT',
    attribute_name: 'Target Segment',
    levels: [
      { level_code: 'SEG_MASS', level_name: 'Mass Market' },
      { level_code: 'SEG_VALUE', level_name: 'Value Seekers' },
      { level_code: 'SEG_PREMIUM', level_name: 'Premium Seekers' },
      { level_code: 'SEG_YOUNG', level_name: 'Young Adults 18-25' },
      { level_code: 'SEG_MATURE', level_name: 'Mature Adults 35+' },
      { level_code: 'SEG_FEMALE', level_name: 'Female Focused' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'SHELF',
    attribute_name: 'Shelf Position',
    levels: [
      { level_code: 'SH_EYE', level_name: 'Eye Level' },
      { level_code: 'SH_REACH', level_name: 'Reach Level' },
      { level_code: 'SH_STOOP', level_name: 'Stoop Level' },
      { level_code: 'SH_ENDCAP', level_name: 'End Cap' },
      { level_code: 'SH_COOLER', level_name: 'Cooler Door' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
];

// Meals de Colombia attributes - ice cream/paletas focused
const MEALS_ATTRIBUTES: SimulationAttribute[] = [
  {
    attribute_code: 'BRAND',
    attribute_name: 'Marca',
    levels: [
      { level_code: 'BR_POLET', level_name: 'Polet', level_description: 'Paletas tradicionales' },
      { level_code: 'BR_CHOCORICO', level_name: 'Choco Rico', level_description: 'Linea infantil' },
      { level_code: 'BR_SUPERVACA', level_name: 'Super Vaca', level_description: 'Helados cremosos' },
      { level_code: 'BR_FRESKITO', level_name: 'Freskito', level_description: 'Formato familiar' },
      { level_code: 'BR_CREMHELADO', level_name: 'Crem Helado', level_description: 'Competidor principal' },
      { level_code: 'BR_CASERO', level_name: 'Casero', level_description: 'Competidor economia' },
    ],
    isIncluded: true,
    selectedLevels: ['BR_POLET', 'BR_CHOCORICO', 'BR_FRESKITO'],
  },
  {
    attribute_code: 'PRICE',
    attribute_name: 'Precio (COP)',
    levels: [
      { level_code: 'P_1000', level_name: '$1,000', value: 1000, is_price_attribute: true },
      { level_code: 'P_1200', level_name: '$1,200', value: 1200, is_price_attribute: true },
      { level_code: 'P_1500', level_name: '$1,500', value: 1500, is_price_attribute: true },
      { level_code: 'P_1800', level_name: '$1,800', value: 1800, is_price_attribute: true },
      { level_code: 'P_2200', level_name: '$2,200', value: 2200, is_price_attribute: true },
      { level_code: 'P_2500', level_name: '$2,500', value: 2500, is_price_attribute: true },
      { level_code: 'P_2800', level_name: '$2,800', value: 2800, is_price_attribute: true },
      { level_code: 'P_3200', level_name: '$3,200', value: 3200, is_price_attribute: true },
      { level_code: 'P_9500', level_name: '$9,500', value: 9500, is_price_attribute: true, level_description: 'Formato 1L' },
      { level_code: 'P_10500', level_name: '$10,500', value: 10500, is_price_attribute: true, level_description: 'Formato 1L' },
    ],
    isIncluded: true,
    selectedLevels: ['P_1200', 'P_1800', 'P_2200', 'P_2500'],
  },
  {
    attribute_code: 'FORMAT',
    attribute_name: 'Formato',
    levels: [
      { level_code: 'FM_PALETA_AGUA', level_name: 'Paleta de Agua', level_description: '65ml' },
      { level_code: 'FM_PALETA_CREMA', level_name: 'Paleta de Crema', level_description: '75ml' },
      { level_code: 'FM_CONO', level_name: 'Cono', level_description: '120ml' },
      { level_code: 'FM_VASO', level_name: 'Vaso', level_description: '150ml' },
      { level_code: 'FM_LITRO', level_name: 'Litro Familiar', level_description: '1000ml' },
      { level_code: 'FM_MULTIPACK', level_name: 'Multipack', level_description: '4-6 unidades' },
    ],
    isIncluded: true,
    selectedLevels: ['FM_PALETA_AGUA', 'FM_PALETA_CREMA', 'FM_CONO'],
  },
  {
    attribute_code: 'FLAVOR',
    attribute_name: 'Sabor',
    levels: [
      { level_code: 'FL_MORA', level_name: 'Mora' },
      { level_code: 'FL_LIMON', level_name: 'Limon' },
      { level_code: 'FL_COCO', level_name: 'Coco' },
      { level_code: 'FL_FRESA', level_name: 'Fresa' },
      { level_code: 'FL_CHOCOLATE', level_name: 'Chocolate' },
      { level_code: 'FL_VAINILLA', level_name: 'Vainilla' },
      { level_code: 'FL_MANGO', level_name: 'Mango' },
      { level_code: 'FL_AREQUIPE', level_name: 'Arequipe' },
      { level_code: 'FL_NAPOLITANO', level_name: 'Napolitano' },
      { level_code: 'FL_GALLETA', level_name: 'Galleta' },
    ],
    isIncluded: true,
    selectedLevels: ['FL_MORA', 'FL_CHOCOLATE', 'FL_VAINILLA'],
  },
  {
    attribute_code: 'POSITIONING',
    attribute_name: 'Posicionamiento',
    levels: [
      { level_code: 'POS_VALUE', level_name: 'Economico', level_description: 'Precio accesible' },
      { level_code: 'POS_MAINSTREAM', level_name: 'Mainstream', level_description: 'Consumo masivo' },
      { level_code: 'POS_PREMIUM', level_name: 'Premium', level_description: 'Alta calidad' },
      { level_code: 'POS_KIDS', level_name: 'Infantil', level_description: 'Para ninos' },
    ],
    isIncluded: true,
    selectedLevels: ['POS_MAINSTREAM', 'POS_KIDS'],
  },
  {
    attribute_code: 'CHANNEL',
    attribute_name: 'Canal',
    levels: [
      { level_code: 'CH_TIENDA', level_name: 'Tienda', level_description: 'Canal tradicional' },
      { level_code: 'CH_SUPER', level_name: 'Supermercado', level_description: 'Autoservicio' },
      { level_code: 'CH_CONVENIENCIA', level_name: 'Conveniencia', level_description: 'Tiendas 24h' },
      { level_code: 'CH_AMBULANTE', level_name: 'Vendedor Ambulante', level_description: 'Carrito de helados' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'OCCASION',
    attribute_name: 'Ocasion',
    levels: [
      { level_code: 'OCC_ANTOJO', level_name: 'Antojo', level_description: 'Impulso diario' },
      { level_code: 'OCC_REFRESCO', level_name: 'Refrescante', level_description: 'Aliviar el calor' },
      { level_code: 'OCC_POSTRE', level_name: 'Postre', level_description: 'Despues de comida' },
      { level_code: 'OCC_CELEBRACION', level_name: 'Celebracion', level_description: 'Fiestas y reuniones' },
      { level_code: 'OCC_FAMILIA', level_name: 'Familiar', level_description: 'En casa con familia' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'PROMO',
    attribute_name: 'Promocion',
    levels: [
      { level_code: 'PRM_NONE', level_name: 'Sin Promocion' },
      { level_code: 'PRM_2X1', level_name: '2x1' },
      { level_code: 'PRM_DESC10', level_name: '10% Descuento' },
      { level_code: 'PRM_DESC20', level_name: '20% Descuento' },
      { level_code: 'PRM_PACK', level_name: 'Pack Familiar' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'CLAIM',
    attribute_name: 'Claim',
    levels: [
      { level_code: 'CL_NATURAL', level_name: 'Ingredientes Naturales' },
      { level_code: 'CL_FRUTA', level_name: 'Con Fruta Real' },
      { level_code: 'CL_BAJO_AZ', level_name: 'Bajo en Azucar' },
      { level_code: 'CL_SIN_LACT', level_name: 'Sin Lactosa' },
      { level_code: 'CL_TRADICION', level_name: 'Receta Tradicional' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'DISPLAY',
    attribute_name: 'Exhibicion',
    levels: [
      { level_code: 'DSP_CONGELADOR', level_name: 'Congelador', level_description: 'Exhibidor estandar' },
      { level_code: 'DSP_PUNTO_PAGO', level_name: 'Punto de Pago', level_description: 'Impulso checkout' },
      { level_code: 'DSP_ISLA', level_name: 'Isla', level_description: 'Exhibicion especial' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
];

// Colas Mexico attributes - real brands for CSD category
const COLAS_MEXICO_ATTRIBUTES: SimulationAttribute[] = [
  {
    attribute_code: 'BRAND',
    attribute_name: 'Marca',
    levels: [
      { level_code: 'BR_COCACOLA', level_name: 'Coca-Cola', level_description: 'Líder del mercado (72% share)' },
      { level_code: 'BR_COCACOLA_SA', level_name: 'Coca-Cola Sin Azúcar', level_description: 'Variante zero calorías' },
      { level_code: 'BR_COCACOLA_ZERO', level_name: 'Coca-Cola Zero', level_description: 'Variante zero azúcar' },
      { level_code: 'BR_PEPSI', level_name: 'Pepsi', level_description: 'Principal competidor (18% share)' },
      { level_code: 'BR_PEPSI_BLACK', level_name: 'Pepsi Black', level_description: 'Variante zero calorías' },
      { level_code: 'BR_REDCOLA', level_name: 'Red Cola', level_description: 'Competidor emergente - Orgullosamente Mexicano' },
      { level_code: 'BR_BIGCOLA', level_name: 'Big Cola', level_description: 'Competidor de valor (Grupo AJE)' },
    ],
    isIncluded: true,
    selectedLevels: ['BR_COCACOLA', 'BR_PEPSI', 'BR_REDCOLA', 'BR_BIGCOLA'],
  },
  {
    attribute_code: 'PRICE',
    attribute_name: 'Precio (MXN)',
    levels: [
      { level_code: 'P_12', level_name: '$12', value: 12, is_price_attribute: true, level_description: 'Lata 235ml' },
      { level_code: 'P_17', level_name: '$17', value: 17, is_price_attribute: true, level_description: 'Lata 355ml' },
      { level_code: 'P_20', level_name: '$20', value: 20, is_price_attribute: true, level_description: 'Barrera psicológica' },
      { level_code: 'P_22', level_name: '$22', value: 22, is_price_attribute: true, level_description: 'PET 600ml' },
      { level_code: 'P_28', level_name: '$28', value: 28, is_price_attribute: true, level_description: 'PET 1L' },
      { level_code: 'P_35', level_name: '$35', value: 35, is_price_attribute: true, level_description: 'PET 1.5L' },
      { level_code: 'P_42', level_name: '$42', value: 42, is_price_attribute: true, level_description: 'PET 2L' },
      { level_code: 'P_55', level_name: '$55', value: 55, is_price_attribute: true, level_description: 'PET 3L' },
    ],
    isIncluded: true,
    selectedLevels: ['P_17', 'P_22', 'P_28', 'P_42'],
  },
  {
    attribute_code: 'FORMAT',
    attribute_name: 'Formato',
    levels: [
      { level_code: 'FM_LATA_235', level_name: 'Lata 235ml', level_description: 'Impulso / Mini' },
      { level_code: 'FM_LATA_355', level_name: 'Lata 355ml', level_description: 'Conveniencia / OXXO' },
      { level_code: 'FM_PET_600', level_name: 'PET 600ml', level_description: 'On-the-go' },
      { level_code: 'FM_PET_1L', level_name: 'PET 1L', level_description: 'Personal grande' },
      { level_code: 'FM_PET_2L', level_name: 'PET 2L', level_description: 'Familiar' },
      { level_code: 'FM_PET_3L', level_name: 'PET 3L', level_description: 'Súper familiar' },
      { level_code: 'FM_RETORNABLE', level_name: 'Vidrio Retornable', level_description: 'Económico tradicional' },
    ],
    isIncluded: true,
    selectedLevels: ['FM_LATA_355', 'FM_PET_600', 'FM_PET_2L'],
  },
  {
    attribute_code: 'VARIANT',
    attribute_name: 'Variante',
    levels: [
      { level_code: 'VAR_ORIGINAL', level_name: 'Original', level_description: 'Fórmula clásica' },
      { level_code: 'VAR_ZERO', level_name: 'Zero / Sin Azúcar', level_description: 'Sin calorías' },
      { level_code: 'VAR_LIGHT', level_name: 'Light', level_description: 'Reducido en calorías' },
      { level_code: 'VAR_CHERRY', level_name: 'Cherry', level_description: 'Sabor cereza' },
      { level_code: 'VAR_VANILLA', level_name: 'Vanilla', level_description: 'Sabor vainilla' },
    ],
    isIncluded: true,
    selectedLevels: ['VAR_ORIGINAL', 'VAR_ZERO'],
  },
  {
    attribute_code: 'CHANNEL',
    attribute_name: 'Canal',
    levels: [
      { level_code: 'CH_OXXO', level_name: 'OXXO', level_description: 'Conveniencia (23,000 tiendas)' },
      { level_code: 'CH_TIENDA', level_name: 'Tienda Tradicional', level_description: 'Canal tradicional' },
      { level_code: 'CH_SUPER', level_name: 'Supermercado', level_description: 'Autoservicio' },
      { level_code: 'CH_CLUB', level_name: 'Club de Precios', level_description: 'Costco, Sam\'s' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
];

// Generic attributes for other clients (neutral)
const GENERIC_ATTRIBUTES: SimulationAttribute[] = [
  {
    attribute_code: 'BRAND',
    attribute_name: 'Brand',
    levels: [
      { level_code: 'BR_A', level_name: 'Brand A', level_description: 'Premium offering' },
      { level_code: 'BR_B', level_name: 'Brand B', level_description: 'Mainstream' },
      { level_code: 'BR_C', level_name: 'Brand C', level_description: 'Value segment' },
      { level_code: 'BR_COMP1', level_name: 'Competitor 1', level_description: 'Main competitor' },
      { level_code: 'BR_COMP2', level_name: 'Competitor 2', level_description: 'Secondary competitor' },
    ],
    isIncluded: true,
    selectedLevels: ['BR_A', 'BR_B'],
  },
  {
    attribute_code: 'PRICE',
    attribute_name: 'Price Point',
    levels: [
      { level_code: 'P_LOW', level_name: 'Low', value: 1, is_price_attribute: true },
      { level_code: 'P_MED_LOW', level_name: 'Medium-Low', value: 2, is_price_attribute: true },
      { level_code: 'P_MED', level_name: 'Medium', value: 3, is_price_attribute: true },
      { level_code: 'P_MED_HIGH', level_name: 'Medium-High', value: 4, is_price_attribute: true },
      { level_code: 'P_HIGH', level_name: 'High', value: 5, is_price_attribute: true },
      { level_code: 'P_PREMIUM', level_name: 'Premium', value: 6, is_price_attribute: true },
    ],
    isIncluded: true,
    selectedLevels: ['P_MED', 'P_MED_HIGH', 'P_HIGH'],
  },
  {
    attribute_code: 'SIZE',
    attribute_name: 'Size',
    levels: [
      { level_code: 'SZ_SMALL', level_name: 'Small', level_description: 'Single serve' },
      { level_code: 'SZ_MEDIUM', level_name: 'Medium', level_description: 'Standard' },
      { level_code: 'SZ_LARGE', level_name: 'Large', level_description: 'Family size' },
    ],
    isIncluded: true,
    selectedLevels: ['SZ_SMALL', 'SZ_MEDIUM'],
  },
  {
    attribute_code: 'VARIANT',
    attribute_name: 'Variant',
    levels: [
      { level_code: 'VAR_ORIGINAL', level_name: 'Original' },
      { level_code: 'VAR_NEW', level_name: 'New Formula' },
      { level_code: 'VAR_LIGHT', level_name: 'Light' },
      { level_code: 'VAR_PREMIUM', level_name: 'Premium' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'CHANNEL',
    attribute_name: 'Channel',
    levels: [
      { level_code: 'CH_MODERN', level_name: 'Modern Trade' },
      { level_code: 'CH_TRADITIONAL', level_name: 'Traditional' },
      { level_code: 'CH_ECOMM', level_name: 'E-commerce' },
      { level_code: 'CH_CONVENIENCE', level_name: 'Convenience' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'PROMO',
    attribute_name: 'Promotion',
    levels: [
      { level_code: 'PRM_NONE', level_name: 'No Promotion' },
      { level_code: 'PRM_10OFF', level_name: '10% Off' },
      { level_code: 'PRM_20OFF', level_name: '20% Off' },
      { level_code: 'PRM_BOGO', level_name: 'Buy 1 Get 1' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
];

// Additional attributes to reach 25 total
const EXTENDED_ATTRIBUTES: SimulationAttribute[] = [
  {
    attribute_code: 'LABEL',
    attribute_name: 'Label Design',
    levels: [
      { level_code: 'LBL_CLASSIC', level_name: 'Classic' },
      { level_code: 'LBL_MODERN', level_name: 'Modern' },
      { level_code: 'LBL_RETRO', level_name: 'Retro' },
      { level_code: 'LBL_MINIMAL', level_name: 'Minimalist' },
      { level_code: 'LBL_PREMIUM', level_name: 'Premium' },
      { level_code: 'LBL_LIMITED', level_name: 'Limited Edition' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'CALORIES',
    attribute_name: 'Calorie Content',
    levels: [
      { level_code: 'CAL_ZERO', level_name: '0 cal' },
      { level_code: 'CAL_LOW', level_name: '<100 cal' },
      { level_code: 'CAL_MED', level_name: '100-150 cal' },
      { level_code: 'CAL_STD', level_name: '150-200 cal' },
      { level_code: 'CAL_HIGH', level_name: '>200 cal' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'CARB',
    attribute_name: 'Carbohydrate Level',
    levels: [
      { level_code: 'CARB_ZERO', level_name: 'Zero Carb' },
      { level_code: 'CARB_LOW', level_name: 'Low Carb (<5g)' },
      { level_code: 'CARB_MED', level_name: 'Medium (5-10g)' },
      { level_code: 'CARB_STD', level_name: 'Standard (10-15g)' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'BREW',
    attribute_name: 'Brewing Method',
    levels: [
      { level_code: 'BRW_LAGER', level_name: 'Lager' },
      { level_code: 'BRW_PILSNER', level_name: 'Pilsner' },
      { level_code: 'BRW_ALE', level_name: 'Ale' },
      { level_code: 'BRW_IPA', level_name: 'IPA' },
      { level_code: 'BRW_WHEAT', level_name: 'Wheat Beer' },
      { level_code: 'BRW_STOUT', level_name: 'Stout' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'GLUTEN',
    attribute_name: 'Gluten Content',
    levels: [
      { level_code: 'GL_FREE', level_name: 'Gluten Free' },
      { level_code: 'GL_REDUCED', level_name: 'Reduced Gluten' },
      { level_code: 'GL_STD', level_name: 'Standard' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'SUSTAIN',
    attribute_name: 'Sustainability',
    levels: [
      { level_code: 'SUS_NONE', level_name: 'No Claims' },
      { level_code: 'SUS_RECYCLE', level_name: 'Recyclable Pack' },
      { level_code: 'SUS_ORGANIC', level_name: 'Organic' },
      { level_code: 'SUS_CARBON', level_name: 'Carbon Neutral' },
      { level_code: 'SUS_LOCAL', level_name: 'Local Ingredients' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'DISPLAY',
    attribute_name: 'In-Store Display',
    levels: [
      { level_code: 'DSP_NONE', level_name: 'No Display' },
      { level_code: 'DSP_FLOOR', level_name: 'Floor Display' },
      { level_code: 'DSP_SHIPPER', level_name: 'Shipper' },
      { level_code: 'DSP_PALLET', level_name: 'Pallet Display' },
      { level_code: 'DSP_COOLER', level_name: 'Dedicated Cooler' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'MEDIA',
    attribute_name: 'Media Support',
    levels: [
      { level_code: 'MED_NONE', level_name: 'No Media' },
      { level_code: 'MED_DIGITAL', level_name: 'Digital Only' },
      { level_code: 'MED_TV', level_name: 'TV Campaign' },
      { level_code: 'MED_OUTDOOR', level_name: 'Outdoor' },
      { level_code: 'MED_FULL', level_name: 'Full Media Mix' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'SEASON',
    attribute_name: 'Seasonality',
    levels: [
      { level_code: 'SZN_YEARROUND', level_name: 'Year Round' },
      { level_code: 'SZN_SUMMER', level_name: 'Summer Limited' },
      { level_code: 'SZN_WINTER', level_name: 'Winter Limited' },
      { level_code: 'SZN_CARNIVAL', level_name: 'Carnival Edition' },
      { level_code: 'SZN_HOLIDAY', level_name: 'Holiday Edition' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
  {
    attribute_code: 'WARRANTY',
    attribute_name: 'Freshness Guarantee',
    levels: [
      { level_code: 'WAR_NONE', level_name: 'No Guarantee' },
      { level_code: 'WAR_30', level_name: '30-Day Fresh' },
      { level_code: 'WAR_60', level_name: '60-Day Fresh' },
      { level_code: 'WAR_BORN', level_name: 'Born-On Date' },
    ],
    isIncluded: false,
    selectedLevels: [],
  },
];

// Get base attributes based on client
function getClientBaseAttributes(clientId: string): SimulationAttribute[] {
  const id = clientId.toLowerCase();
  // Meals de Colombia
  if (id.includes('meals')) {
    return MEALS_ATTRIBUTES;
  }
  // Colas Mexico (generic or FEMSA-specific)
  if (id.includes('colas') || id.includes('coca-cola')) {
    return COLAS_MEXICO_ATTRIBUTES;
  }
  // ABB / Beer clients - use default beer attributes
  if (id.includes('abb') || id.includes('cervecera') || id.includes('beer')) {
    return [...DEFAULT_ATTRIBUTES, ...EXTENDED_ATTRIBUTES];
  }
  // Default to generic for unknown clients
  return GENERIC_ATTRIBUTES;
}

// Get initial attribute configuration based on simulation type and client
function getInitialAttributes(simulationType: string, clientId: string = ''): SimulationAttribute[] {
  // Use client-specific attributes if available, otherwise use default (beer)
  const clientAttributes = clientId ? getClientBaseAttributes(clientId) : [...DEFAULT_ATTRIBUTES, ...EXTENDED_ATTRIBUTES];
  const baseAttributes = clientId ? clientAttributes : [...DEFAULT_ATTRIBUTES, ...EXTENDED_ATTRIBUTES];

  // Helper to select first N levels from an attribute
  const selectFirstN = (attr: SimulationAttribute, n: number) =>
    attr.levels.slice(0, n).map((l) => l.level_code);

  // Apply simulation-type-specific presets (generic - works with any client's attributes)
  switch (simulationType) {
    case 'market_entry':
      // Market Entry: Focus on new product testing with fewer pre-selected options
      return baseAttributes.map((attr) => {
        if (attr.attribute_code === 'BRAND') {
          return { ...attr, isIncluded: true, isExpanded: true, selectedLevels: selectFirstN(attr, 2) };
        }
        if (attr.attribute_code === 'PRICE') {
          const priceLevels = attr.levels.filter((l) => l.is_price_attribute);
          const midPrices = priceLevels.slice(Math.floor(priceLevels.length * 0.3), Math.floor(priceLevels.length * 0.7));
          return { ...attr, isIncluded: true, selectedLevels: midPrices.map((l) => l.level_code) };
        }
        if (attr.attribute_code === 'PACK_SIZE' || attr.attribute_code === 'FORMAT' || attr.attribute_code === 'SIZE') {
          return { ...attr, isIncluded: true, selectedLevels: selectFirstN(attr, 2) };
        }
        return { ...attr, isIncluded: false, selectedLevels: [] };
      });

    case 'portfolio_optimization':
      // Portfolio Optimization: Include full portfolio to find optimal mix
      return baseAttributes.map((attr) => {
        if (attr.attribute_code === 'BRAND') {
          return { ...attr, isIncluded: true, isExpanded: true, selectedLevels: selectFirstN(attr, 5) };
        }
        if (attr.attribute_code === 'PRICE') {
          return { ...attr, isIncluded: true, selectedLevels: selectFirstN(attr, 6) };
        }
        if (attr.attribute_code === 'PACK_SIZE' || attr.attribute_code === 'FORMAT' || attr.attribute_code === 'SIZE') {
          return { ...attr, isIncluded: true, selectedLevels: selectFirstN(attr, 4) };
        }
        if (attr.attribute_code === 'SEGMENT' || attr.attribute_code === 'POSITIONING') {
          return { ...attr, isIncluded: true, selectedLevels: attr.levels.map((l) => l.level_code) };
        }
        return { ...attr, isIncluded: false, selectedLevels: [] };
      });

    case 'sku_rationalization':
      // SKU Rationalization: Focus on existing SKUs to identify redundancy
      return baseAttributes.map((attr) => {
        if (attr.attribute_code === 'BRAND') {
          // Select own brands (first 3, excluding competitors which are typically last)
          return { ...attr, isIncluded: true, isExpanded: true, selectedLevels: selectFirstN(attr, 3) };
        }
        if (attr.attribute_code === 'PACK_SIZE' || attr.attribute_code === 'FORMAT' || attr.attribute_code === 'SIZE') {
          return { ...attr, isIncluded: true, isExpanded: true, selectedLevels: selectFirstN(attr, 5) };
        }
        if (attr.attribute_code === 'PACK_TYPE' || attr.attribute_code === 'FLAVOR') {
          return { ...attr, isIncluded: true, selectedLevels: selectFirstN(attr, 3) };
        }
        return { ...attr, isIncluded: false, selectedLevels: [] };
      });

    case 'pricing':
      // Price Optimization: Focus on price testing across brands
      return baseAttributes.map((attr) => {
        if (attr.attribute_code === 'BRAND') {
          return { ...attr, isIncluded: true, isExpanded: false, selectedLevels: selectFirstN(attr, 2) };
        }
        if (attr.attribute_code === 'PRICE') {
          // Select all price levels for comprehensive testing
          return { ...attr, isIncluded: true, isExpanded: true, selectedLevels: attr.levels.map((l) => l.level_code) };
        }
        if (attr.attribute_code === 'PACK_SIZE' || attr.attribute_code === 'FORMAT' || attr.attribute_code === 'SIZE') {
          return { ...attr, isIncluded: true, selectedLevels: selectFirstN(attr, 1) }; // Fixed size
        }
        return { ...attr, isIncluded: false, selectedLevels: [] };
      });

    case 'launch':
      // Launch Planning: New product concept testing
      return baseAttributes.map((attr) => {
        if (attr.attribute_code === 'BRAND') {
          return { ...attr, isIncluded: true, isExpanded: true, selectedLevels: selectFirstN(attr, 1) }; // New launch brand
        }
        if (attr.attribute_code === 'PRICE') {
          // Select premium price range
          const priceLevels = attr.levels.filter((l) => l.is_price_attribute);
          const upperPrices = priceLevels.slice(Math.floor(priceLevels.length * 0.5), Math.floor(priceLevels.length * 0.8));
          return { ...attr, isIncluded: true, selectedLevels: upperPrices.map((l) => l.level_code) };
        }
        if (attr.attribute_code === 'PACK_SIZE' || attr.attribute_code === 'FORMAT' || attr.attribute_code === 'SIZE') {
          return { ...attr, isIncluded: true, selectedLevels: selectFirstN(attr, 3) };
        }
        if (attr.attribute_code === 'FLAVOR') {
          return { ...attr, isIncluded: true, selectedLevels: selectFirstN(attr, 2) };
        }
        return { ...attr, isIncluded: false, selectedLevels: [] };
      });

    default:
      return baseAttributes;
  }
}

export function SimulationConfigurator({ clientId, simulationType, onRunSimulation, language = 'en' }: SimulationConfiguratorProps) {
  const isEs = language === 'es';
  const [attributes, setAttributes] = useState<SimulationAttribute[]>(() =>
    getInitialAttributes(simulationType, clientId)
  );
  const [simulationName, setSimulationName] = useState('');
  const [showAddAttribute, setShowAddAttribute] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [simulationResult, setSimulationResult] = useState<SimulationResultData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const includedAttributes = attributes.filter((a) => a.isIncluded);
  const availableAttributes = attributes.filter((a) => !a.isIncluded);

  const toggleAttributeExpand = (attrCode: string) => {
    setAttributes((prev) =>
      prev.map((a) => (a.attribute_code === attrCode ? { ...a, isExpanded: !a.isExpanded } : a))
    );
  };

  const toggleAttributeInclude = (attrCode: string) => {
    setAttributes((prev) =>
      prev.map((a) =>
        a.attribute_code === attrCode
          ? { ...a, isIncluded: !a.isIncluded, isExpanded: !a.isIncluded ? true : a.isExpanded }
          : a
      )
    );
  };

  const toggleLevel = (attrCode: string, levelCode: string) => {
    setAttributes((prev) =>
      prev.map((a) => {
        if (a.attribute_code !== attrCode) return a;
        const selected = a.selectedLevels || [];
        const newSelected = selected.includes(levelCode)
          ? selected.filter((l) => l !== levelCode)
          : [...selected, levelCode];
        return { ...a, selectedLevels: newSelected };
      })
    );
  };

  const selectAllLevels = (attrCode: string) => {
    setAttributes((prev) =>
      prev.map((a) =>
        a.attribute_code === attrCode ? { ...a, selectedLevels: a.levels.map((l) => l.level_code) } : a
      )
    );
  };

  const clearAllLevels = (attrCode: string) => {
    setAttributes((prev) =>
      prev.map((a) => (a.attribute_code === attrCode ? { ...a, selectedLevels: [] } : a))
    );
  };

  const totalCombinations = includedAttributes.reduce((acc, attr) => {
    const count = attr.selectedLevels?.length || 0;
    return count > 0 ? acc * count : acc;
  }, 1);

  const handleRunSimulation = async () => {
    const config: SimulationConfig = {
      name: simulationName || `${simulationType} Simulation`,
      simulation_type: simulationType as SimulationConfig['simulation_type'],
      attributes: includedAttributes.filter((a) => (a.selectedLevels?.length || 0) > 0),
    };

    setIsRunning(true);
    setError(null);

    try {
      const response = await fetch(`http://localhost:3001/api/clients/${clientId}/simulations/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });

      if (!response.ok) {
        throw new Error(`Simulation failed: ${response.statusText}`);
      }

      const result = await response.json();
      setSimulationResult(result);
      onRunSimulation?.(config, result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Simulation failed');
      console.error('Simulation error:', err);
    } finally {
      setIsRunning(false);
    }
  };

  const handleCloseResults = () => {
    setSimulationResult(null);
  };

  // Build lookup maps for attribute/level names
  const attributeNames: Record<string, string> = {};
  const levelNames: Record<string, Record<string, string>> = {};
  for (const attr of attributes) {
    attributeNames[attr.attribute_code] = attr.attribute_name;
    levelNames[attr.attribute_code] = {};
    for (const level of attr.levels) {
      levelNames[attr.attribute_code][level.level_code] = level.level_name;
    }
  }

  // Show results if available
  if (simulationResult) {
    return (
      <SimulationResults
        result={simulationResult}
        attributeNames={attributeNames}
        levelNames={levelNames}
        onClose={handleCloseResults}
      />
    );
  }

  // Simulation type context info
  const simulationContexts: Record<string, { title: string; titleEs: string; description: string; descriptionEs: string; icon: string; guidance: string; guidanceEs: string }> = {
    'market_entry': {
      title: 'Market Entry Simulation',
      titleEs: 'Simulacion de Entrada al Mercado',
      description: 'Evaluate potential product configurations for entering a new market or segment',
      descriptionEs: 'Evalua configuraciones de producto potenciales para entrar a un nuevo mercado o segmento',
      icon: '🚀',
      guidance: 'Select the attributes and levels you want to test. Each combination represents a potential product configuration.',
      guidanceEs: 'Selecciona los atributos y niveles que deseas probar. Cada combinacion representa una configuracion de producto potencial.',
    },
    'portfolio_optimization': {
      title: 'Portfolio Optimization',
      titleEs: 'Optimizacion de Portafolio',
      description: 'Analyze your current portfolio to identify the optimal SKU mix',
      descriptionEs: 'Analiza tu portafolio actual para identificar la mezcla optima de SKUs',
      icon: '📊',
      guidance: 'Include your existing SKUs and potential additions. The simulation will identify the best portfolio composition.',
      guidanceEs: 'Incluye tus SKUs existentes y adiciones potenciales. La simulacion identificara la mejor composicion de portafolio.',
    },
    'sku_rationalization': {
      title: 'SKU Rationalization',
      titleEs: 'Racionalizacion de SKU',
      description: 'Identify underperforming SKUs and consolidation opportunities',
      descriptionEs: 'Identifica SKUs con bajo rendimiento y oportunidades de consolidacion',
      icon: '🔄',
      guidance: 'Select your current SKUs to analyze. The simulation will show overlap and recommend rationalization.',
      guidanceEs: 'Selecciona tus SKUs actuales para analizar. La simulacion mostrara traslapes y recomendara racionalizacion.',
    },
    'pricing': {
      title: 'Price Optimization',
      titleEs: 'Optimizacion de Precio',
      description: 'Find optimal price points across your product range',
      descriptionEs: 'Encuentra puntos de precio optimos en tu rango de productos',
      icon: '💰',
      guidance: 'Include multiple price levels to test. Focus on the Price attribute with different brand/pack combinations.',
      guidanceEs: 'Incluye multiples niveles de precio para probar. Enfocate en el atributo de Precio con diferentes combinaciones de marca/empaque.',
    },
    'launch': {
      title: 'Launch Planning',
      titleEs: 'Planeacion de Lanzamiento',
      description: 'Simulate new product launch scenarios and market impact',
      descriptionEs: 'Simula escenarios de lanzamiento de nuevos productos e impacto en el mercado',
      icon: '✨',
      guidance: 'Configure your new product concept. Compare against existing competitive products.',
      guidanceEs: 'Configura tu concepto de nuevo producto. Compara contra productos competitivos existentes.',
    },
  };

  const contextData = simulationContexts[simulationType] || simulationContexts['market_entry'];
  const context = {
    title: isEs ? contextData.titleEs : contextData.title,
    description: isEs ? contextData.descriptionEs : contextData.description,
    icon: contextData.icon,
    guidance: isEs ? contextData.guidanceEs : contextData.guidance,
  };

  // Validation: Check if we have meaningful combinations (at least 2)
  const hasValidConfig = totalCombinations >= 2;
  const validationMessage = !hasValidConfig
    ? (isEs ? 'Selecciona al menos 2 niveles en tus atributos para crear multiples escenarios' : 'Select at least 2 levels across your attributes to create multiple scenarios')
    : totalCombinations > 10000
    ? (isEs ? `Simulacion grande (${totalCombinations.toLocaleString()} escenarios) - puede tardar mas` : `Large simulation (${totalCombinations.toLocaleString()} scenarios) - may take longer`)
    : null;

  return (
    <div className="space-y-6">
      {/* Simulation Type Context */}
      <div className="card p-4 bg-surface-600/50 border-l-4 border-primary-500">
        <div className="flex items-start gap-3">
          <span className="text-2xl">{context.icon}</span>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white">{context.title}</h3>
            <p className="text-sm text-text-secondary mt-1">{context.description}</p>
            <p className="text-xs text-text-tertiary mt-2 italic">{context.guidance}</p>
          </div>
        </div>
      </div>

      {/* Header with simulation name */}
      <div className="card p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder={isEs ? 'Ingresa nombre de simulacion...' : 'Enter simulation name...'}
              value={simulationName}
              onChange={(e) => setSimulationName(e.target.value)}
              className="w-full bg-surface-700 border border-surface-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
            />
          </div>
          <div className="text-sm text-text-secondary">
            <span className="text-primary-400 font-semibold">{includedAttributes.length}</span> {isEs ? 'atributos' : 'attributes'} |{' '}
            <span className={`font-semibold ${hasValidConfig ? 'text-primary-400' : 'text-warning-400'}`}>
              {totalCombinations.toLocaleString()}
            </span> {isEs ? 'combinaciones' : 'combinations'}
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-primary-400">{includedAttributes.length}</div>
          <div className="text-xs text-text-secondary mt-1">{isEs ? 'Atributos Activos' : 'Active Attributes'}</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-primary-400">
            {includedAttributes.reduce((sum, a) => sum + (a.selectedLevels?.length || 0), 0)}
          </div>
          <div className="text-xs text-text-secondary mt-1">{isEs ? 'Niveles Totales' : 'Total Levels'}</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-emerald-400">{totalCombinations.toLocaleString()}</div>
          <div className="text-xs text-text-secondary mt-1">{isEs ? 'Combinaciones de Producto' : 'Product Combinations'}</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-amber-400">{availableAttributes.length}</div>
          <div className="text-xs text-text-secondary mt-1">{isEs ? 'Disponibles para Agregar' : 'Available to Add'}</div>
        </div>
      </div>

      {/* Active Attributes Grid */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">{isEs ? 'Impulsores de Decision (Atributos)' : 'Decision Drivers (Attributes)'}</h3>
          <button
            onClick={() => setShowAddAttribute(!showAddAttribute)}
            className="btn btn-secondary text-sm flex items-center gap-2"
          >
            <PlusIcon className="w-4 h-4" />
            {isEs ? 'Agregar Atributo' : 'Add Attribute'}
          </button>
        </div>

        {/* Add Attribute Dropdown */}
        {showAddAttribute && availableAttributes.length > 0 && (
          <div className="mb-4 p-4 bg-surface-700/50 rounded-lg border border-surface-600">
            <div className="text-sm text-text-secondary mb-2">{isEs ? 'Selecciona atributos para agregar:' : 'Select attributes to add:'}</div>
            <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
              {availableAttributes.map((attr) => (
                <button
                  key={attr.attribute_code}
                  onClick={() => {
                    toggleAttributeInclude(attr.attribute_code);
                  }}
                  className="text-left px-3 py-2 bg-surface-600 hover:bg-surface-500 rounded text-sm text-text-secondary hover:text-white transition-colors"
                >
                  {attr.attribute_name}
                  <span className="text-text-tertiary ml-1">({attr.levels.length})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Attributes List */}
        <div className="space-y-2">
          {includedAttributes.length === 0 ? (
            <div className="text-center py-8 text-text-tertiary">
              {isEs ? 'No hay atributos seleccionados. Haz clic en "Agregar Atributo" para comenzar.' : 'No attributes selected. Click "Add Attribute" to get started.'}
            </div>
          ) : (
            includedAttributes.map((attr, index) => (
              <div key={attr.attribute_code} className="bg-surface-700/50 rounded-lg overflow-hidden">
                {/* Attribute Header */}
                <div
                  className="flex items-center gap-3 p-3 cursor-pointer hover:bg-surface-600/50"
                  onClick={() => toggleAttributeExpand(attr.attribute_code)}
                >
                  <div className="text-text-tertiary w-6 text-center text-sm">{index + 1}</div>
                  {attr.isExpanded ? (
                    <ChevronDownIcon className="w-4 h-4 text-text-secondary" />
                  ) : (
                    <ChevronRightIcon className="w-4 h-4 text-text-secondary" />
                  )}
                  <div className="flex-1">
                    <span className="font-medium text-white">{attr.attribute_name}</span>
                    <span className="text-text-tertiary ml-2 text-sm">
                      {attr.selectedLevels?.length || 0} / {attr.levels.length} {isEs ? 'niveles seleccionados' : 'levels selected'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {attr.levels.some((l) => l.is_price_attribute) && (
                      <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">Price</span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleAttributeInclude(attr.attribute_code);
                      }}
                      className="p-1 hover:bg-red-500/20 rounded text-text-secondary hover:text-red-400"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Levels Grid */}
                {attr.isExpanded && (
                  <div className="p-3 pt-0 border-t border-surface-600/50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-text-tertiary">{isEs ? 'Selecciona niveles para incluir en la simulacion:' : 'Select levels to include in simulation:'}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => selectAllLevels(attr.attribute_code)}
                          className="text-xs text-primary-400 hover:text-primary-300"
                        >
                          {isEs ? 'Seleccionar Todo' : 'Select All'}
                        </button>
                        <span className="text-surface-400">|</span>
                        <button
                          onClick={() => clearAllLevels(attr.attribute_code)}
                          className="text-xs text-text-secondary hover:text-text-secondary"
                        >
                          {isEs ? 'Limpiar' : 'Clear'}
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {attr.levels.map((level) => {
                        const isSelected = attr.selectedLevels?.includes(level.level_code);
                        return (
                          <button
                            key={level.level_code}
                            onClick={() => toggleLevel(attr.attribute_code, level.level_code)}
                            className={`px-3 py-2 rounded text-sm text-left transition-all ${
                              isSelected
                                ? 'bg-primary-500/20 border border-primary-500/50 text-primary-300'
                                : 'bg-surface-600/50 border border-transparent text-text-secondary hover:bg-surface-500/50 hover:text-text-secondary'
                            }`}
                          >
                            <div className="font-medium truncate">{level.level_name}</div>
                            {level.level_description && (
                              <div className="text-xs text-text-tertiary truncate">{level.level_description}</div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="card p-4 bg-red-500/10 border border-red-500/30">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Validation Message */}
      {validationMessage && (
        <div className={`card p-3 ${
          !hasValidConfig ? 'bg-warning-500/10 border border-warning-500/30' : 'bg-primary-500/10 border border-primary-500/30'
        }`}>
          <p className={`text-sm ${!hasValidConfig ? 'text-warning-400' : 'text-primary-400'}`}>
            {!hasValidConfig ? '⚠️ ' : '💡 '}{validationMessage}
          </p>
        </div>
      )}

      {/* Run Simulation Button */}
      <div className="flex justify-end gap-4">
        <button className="btn btn-secondary">{isEs ? 'Guardar Configuracion' : 'Save Configuration'}</button>
        <button
          onClick={handleRunSimulation}
          disabled={includedAttributes.length === 0 || !hasValidConfig || isRunning}
          className="btn btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRunning ? (
            <>
              <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {isEs ? 'Ejecutando Simulacion...' : 'Running Simulation...'}
            </>
          ) : !hasValidConfig ? (
            <>
              <PlayIcon className="w-5 h-5" />
              {isEs ? 'Selecciona Mas Niveles' : 'Select More Levels'}
            </>
          ) : (
            <>
              <PlayIcon className="w-5 h-5" />
              {isEs ? `Ejecutar Simulacion (${totalCombinations.toLocaleString()} escenarios)` : `Run Simulation (${totalCombinations.toLocaleString()} scenarios)`}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default SimulationConfigurator;
